import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            errorMessage: error?.message || 'An unexpected error occurred.',
        };
    }

    componentDidCatch(error, errorInfo) {
        if (process.env.NODE_ENV === 'development') {
			console.warn('⚠️ Error caught in ErrorBoundary:', error, errorInfo);
			}

        // ✅ Store error logs in sessionStorage (clears on page refresh)
        const errorLog = {
            message: error?.message || 'No error message provided.',
            stack: error?.stack || 'No stack trace available.',
            componentStack: errorInfo?.componentStack || 'No component stack available.',
            timestamp: new Date().toISOString(),
        };

        const storedErrors = JSON.parse(sessionStorage.getItem('errorLogs')) || [];
        storedErrors.push(errorLog);
        sessionStorage.setItem('errorLogs', JSON.stringify(storedErrors.slice(-5))); // Keep only last 5 errors
    }

    handleReload = () => {
        this.setState({ hasError: false }); // Reset error state
        window.location.reload(); // Refresh the page
    };

    handleGoBack = () => {
        this.setState({ hasError: false }); // Reset error state
        window.history.back(); // Go back to the previous page
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={styles.container}>
                    <h1 style={styles.heading}>Something Went Wrong</h1>
                    <p style={styles.message}>{this.state.errorMessage}</p>

                    <div style={styles.buttonContainer}>
                        <button style={styles.button} onClick={this.handleReload}>
                            🔄 Reload Page
                        </button>
                        <button style={styles.secondaryButton} onClick={this.handleGoBack}>
                            ⬅️ Go Back
                        </button>
                    </div>

                    <p style={styles.info}>
                        If the issue persists, please contact support.
                    </p>
                </div>
            );
        }

        return this.props.children;
    }
}

// ✅ Updated Styles for Better UI
const styles = {
    container: {
        textAlign: 'center',
        padding: '40px',
        backgroundColor: '#fff',
        color: '#333',
        maxWidth: '600px',
        margin: '50px auto',
        borderRadius: '8px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
        color: '#dc3545', // Red for error indication
    },
    message: {
        fontSize: '18px',
        marginBottom: '20px',
        color: '#444',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        marginTop: '15px',
    },
    button: {
        padding: '10px 15px',
        fontSize: '16px',
        backgroundColor: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    secondaryButton: {
        padding: '10px 15px',
        fontSize: '16px',
        backgroundColor: '#6c757d',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    info: {
        marginTop: '15px',
        fontSize: '14px',
        color: '#666',
    },
};

export default ErrorBoundary;
